import { AlertCircle } from "lucide-react";
import { useState } from "react";

import { useIsSuperAdmin } from "@/api/is-superadmin";
import { SearchBar } from "@/components/search-bar";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { TabNavigation } from "@/components/ui/tab-navigation";
import { usePlaylists } from "@/features/playlists/api/featch-users-playlists";
import { CreatePlaylistModal } from "@/features/playlists/components/create-playlist-modal";
import { NoPlaylists } from "@/features/playlists/components/no-playlists";
import { PlaylistCard } from "@/features/playlists/components/playlist-card";
import { RecordingItemSkeleton } from "@/features/recordings/components/recording-item-skeleton";
import { OnboardingHeader } from "@/features/registration/components/onboarding-header";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";

export function PlaylistsPage() {
  const { organisation } = useOrganisation();
  const [search, setSearch] = useState<string | null>(null);
  const { isSuperAdmin, isLoading: isSuperAdminLoading } = useIsSuperAdmin();

  const {
    error,
    isLoading: isLoadingPlaylists,
    isError: isErrorPlaylists,
    isSuccess: isSuccessPlaylists,
    data: playlistsData,
  } = usePlaylists(organisation.id);

  const isLoading = isSuperAdminLoading || isLoadingPlaylists;

  const navigationItems = [
    { label: "All Recordings", to: "/recordings/all" },
    { label: "Playlists", to: "/recordings/playlists" },
    ...(isSuperAdmin
      ? [
          {
            label: "Storage",
            to: "/recordings/storage",
          },
        ]
      : []),
  ];

  if (isLoading)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View your organisations recordings"
          className="mb-6"
        />
        <TabNavigation className="mb-6" options={navigationItems} />
        <SearchBar search={search} setSearch={setSearch} className="mb-12" />

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
          <RecordingItemSkeleton />
        </div>
      </section>
    );

  if (isErrorPlaylists || !isSuccessPlaylists)
    return (
      <section>
        <OnboardingHeader
          title="Replay"
          description="View your organisations recordings"
          className="mb-6"
        />
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            {error?.message ?? "An unexpected error occurred"}
          </AlertDescription>
        </Alert>
      </section>
    );

  return (
    <section>
      <OnboardingHeader
        title="Replay"
        description="View your organisations recordings"
        className="mb-6"
      />
      <div className="flex flex-row items-center justify-between">
        <TabNavigation className="mb-6" options={navigationItems} />
        <CreatePlaylistModal isEmpty={false} />
      </div>
      <SearchBar search={search} setSearch={setSearch} className="mb-12" />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {playlistsData.map((playlist) => (
          <PlaylistCard key={playlist.id} playlist={playlist} />
        ))}
      </div>

      {playlistsData.length === 0 && <NoPlaylists />}
    </section>
  );
}
